import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, List, ListItemButton, ListItemText, Stack, Typography } from '@mui/material';

export interface SourceDocument {
  id?: string;
  title?: string;
  publisher?: string;
  company?: string;
  corpus?: string;
  page?: number | string | null;
  url?: string;
  excerpt?: string;
  content?: string;
  source_index?: string;
  document_id?: string;
  chunk_id?: string;
  context_passages?: { chunk_id?: string; page?: number | string | null; chunk_index?: number; text: string }[];
  metadata?: Record<string, any>;
}

function safeUrl(value: unknown): string {
  if (typeof value !== 'string') return '';
  try { const url = new URL(value); return ['https:', 'http:'].includes(url.protocol) ? url.href : ''; }
  catch { return ''; }
}

export default function SourceDocumentsDialog({ open, onClose, documents }: {
  open: boolean; onClose: () => void; documents: SourceDocument[];
}) {
  const [selected, setSelected] = useState(0);
  const groups = useMemo(() => {
    const grouped = new Map<string, { title: string; publisher: string; url: string; category: string; passages: { id: string; page: string; text: string }[] }>();
    documents.forEach((doc, index) => {
      const m = doc.metadata || {};
      const title = doc.title || m.title || m.file_name || doc.company || `Source ${index + 1}`;
      const url = safeUrl(doc.url || m.source_url || m.url);
      const publisher = doc.publisher || m.publisher || doc.company || '';
      const identity = doc.document_id || m.document_id || url || m.file_name || doc.title || m.title;
      const key = `${doc.source_index || m.source_index || ''}:${identity || `passage-${index}`}`;
      const group: { title: string; publisher: string; url: string; category: string; passages: { id: string; page: string; text: string }[] } = grouped.get(key) || { title, publisher, url, category: doc.corpus || m.source_type || '', passages: [] };
      const start = doc.page ?? m.page_num ?? m.page_start ?? m.page;
      const page = start != null ? `${start}${m.page_end != null && m.page_end !== start ? `–${m.page_end}` : ''}` : '';
      const text = doc.context_passages?.length
        ? doc.context_passages.map(p => `${p.page != null ? `Page ${p.page} · ` : ''}${p.chunk_id && p.chunk_id === doc.chunk_id ? 'Cited passage' : 'Surrounding context'}\n${p.text}`).join('\n\n')
        : doc.excerpt || doc.content || '';
      const id = doc.id || m.citation_id || `Source ${index + 1}`;
      // Keep citation IDs intact; collapse only identical source records.
      if (!group.passages.some(p => p.id === id && p.page === page && p.text === text)) group.passages.push({ id, page, text });
      grouped.set(key, group);
    });
    return Array.from(grouped.values());
  }, [documents]);
  useEffect(() => { setSelected(0); }, [documents, open]);
  const active = groups[selected] || groups[0];
  return <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth aria-labelledby="source-documents-title">
    <DialogTitle id="source-documents-title">Source documents ({groups.length})</DialogTitle>
    <DialogContent dividers>
      {!active ? <Alert severity="info">No source documents are available for this answer.</Alert> : <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
        <List aria-label="Source documents" sx={{ width: { xs: '100%', md: 300 }, flexShrink: 0, maxHeight: { xs: 200, md: '65vh' }, overflowY: 'auto' }}>
          {groups.map((group, index) => <ListItemButton key={index} selected={index === selected} onClick={() => setSelected(index)}>
            <ListItemText primary={group.title} secondary={`${group.publisher}${group.publisher ? ' · ' : ''}${group.passages.length} passage${group.passages.length === 1 ? '' : 's'}`} primaryTypographyProps={{ sx: { overflowWrap: 'anywhere' } }} />
          </ListItemButton>)}
        </List>
        <Box sx={{ flex: 1, minWidth: 0, maxHeight: '65vh', overflowY: 'auto' }}>
          <Typography variant="h6" sx={{ overflowWrap: 'anywhere' }}>{active.title}</Typography>
          {active.publisher && <Typography color="text.secondary">{active.publisher}</Typography>}
          {active.category && <Typography component="span" variant="caption" sx={{ display: 'inline-block', my: 1, px: 1.25, py: 0.5, borderRadius: 4, bgcolor: 'action.selected', color: 'text.secondary' }}>{active.category.replace(/_/g, ' ')}</Typography>}
          {active.url ? <Box sx={{ my: 1 }}><Button component="a" href={active.url} target="_blank" rel="noopener noreferrer" variant="outlined">Open original document</Button></Box> : <Typography variant="body2" color="text.secondary" sx={{ my: 1 }}>An original document link is not available for this source.</Typography>}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Cited passages are shown with retrieved surrounding text where available. Page labels identify the cited passage and its context. Older saved answers may contain only a short excerpt.</Typography>
          <Stack spacing={2}>
            {active.passages.map((passage, index) => <Box key={index}>
              <Divider sx={{ mb: 1 }} />
              <Typography fontWeight={600}>[{passage.id}]{passage.page ? ` · Page ${passage.page}` : ''}</Typography>
              <Typography sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', mt: 1 }}>{passage.text || 'No passage text is available.'}</Typography>
            </Box>)}
          </Stack>
        </Box>
      </Box>}
    </DialogContent>
    <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
  </Dialog>;
}
